import {
    Alert, Autocomplete, Box, Checkbox, Chip, ListItemText, MenuItem, Stack,
    TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import React from 'react';

import { DataAPIContext } from '../../api';
import { GET_LIST } from '../../api/dataProvider';

// Builder for a tag condition: a set of tag chips, ANDed or ORed, plus
// (optionally) the tag sources whose tags count as "present".
//
// Two chip kinds exist in the backend:
//  - a concrete tag picked by id ({tag_id}) — matches only that exact tag row;
//  - a (category_code, tag_name) spec, re-resolved against the delivering
//    tenant.
//
// CP-8268: on the BLACKLIST host this builder only *creates* the first kind.
// Spec chips there are an Ops Console concept — they exist so one system-level
// rule can match "vip:gold" in whichever tenant happens to have such a tag; on
// an operator's own rule they buy nothing and the old "Any operator" framing
// invited reasoning about other operators' tag namespaces (the backend rejects
// newly minted ones from the dashboard too). Spec chips already on the rule
// still render and are still saved — dropping them silently would disarm a
// console-authored condition the first time an operator edited an unrelated
// field. The notification-rule host (CP-8264 "tours matched by tag") passes
// `allowSpecChips`: by-name chips are a deliberate capability there (the tag may
// not exist yet), now labelled neutrally without the "Any operator" wording.
//
// Value shape (owned by the caller):
//   { chips: [{ tag_id } | { category_code, tag_name }], match_mode, sources }
//
// Hosts (CP-8264): blacklist rules (the default `resource`) and the
// notification rule editor's "tours matched by tag" condition, which fixes the
// source to tours server-side and passes `hideSources` — the `tag_categories/`
// + `tag_options/` lookup actions are mounted per edit resource so each host
// only needs its own permission scope.

const SCOPE_TENANT = 'tenant';
const SCOPE_ANY = 'any';

export const EMPTY_TAG_CONDITION = { chips: [], match_mode: 'and', sources: ['booking'] };

export const chipKey = (chip) => (
    chip.tag_id != null
        ? `tag:${chip.tag_id}`
        : `code:${chip.category_code} ${String(chip.tag_name).toLowerCase()}`
);

/** "VIP · Gold" for a spec chip, "#Gold (VIP)" for a pinned one. */
export const chipLabel = (chip, tagsById) => {
    if (chip.tag_id != null) {
        const t = tagsById[chip.tag_id];
        return t ? `${t.category.name || t.category.code} · ${t.name}` : `#${chip.tag_id}`;
    }
    return `${chip.category_code} · ${chip.tag_name}`;
};

const TagConditionField = ({
    value, onChange, registry, error,
    resource = 'blacklist_rule',
    hideSources = false,
    allowSpecChips = false,
    caption = 'Blocks every delivery of a booking whose context carries these tags.',
}) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { chips = [], match_mode: matchMode = 'and', sources = [] } = value || {};

    const [scope, setScope] = React.useState(SCOPE_TENANT);
    const [category, setCategory] = React.useState(null);   // {id, code, name} | null
    const [categoryCode, setCategoryCode] = React.useState('');
    const [tag, setTag] = React.useState(null);             // {id, name} | null
    const [tagName, setTagName] = React.useState('');

    const [categories, setCategories] = React.useState([]);
    const [tagOptions, setTagOptions] = React.useState([]);
    // Resolved tags, so a pinned chip can be labelled without a second lookup.
    const [tagsById, setTagsById] = React.useState({});

    // Without allowSpecChips there is no second scope to be in (CP-8268).
    const isTenantScope = !allowSpecChips || scope === SCOPE_TENANT;

    // The tenant's own categories (never another tenant's — the edit API scopes it).
    React.useEffect(() => {
        dataAPI(GET_LIST, resource, {
            urlSuffix: 'tag_categories/', pagination: { page: 1, perPage: 100 }, filter: {},
        })
            .then((res) => setCategories(res.data || []))
            .catch(() => setCategories([]));
    }, [dataAPI, resource]);

    // Tags of the picked category (by id on the tenant path, by code otherwise).
    const activeCode = isTenantScope ? (category && category.code) : categoryCode.trim();
    const activeCategoryId = isTenantScope && category ? category.id : null;
    React.useEffect(() => {
        if (!activeCategoryId && !activeCode) { setTagOptions([]); return; }
        const params = activeCategoryId
            ? { category_id: activeCategoryId }
            : { category_code: activeCode };
        dataAPI(GET_LIST, resource, {
            urlSuffix: 'tag_options/', pagination: { page: 1, perPage: 100 }, filter: params,
        })
            .then((res) => {
                const rows = res.data || [];
                setTagOptions(rows);
                setTagsById((prev) => rows.reduce((acc, t) => ({ ...acc, [t.id]: t }), { ...prev }));
            })
            .catch(() => setTagOptions([]));
    }, [dataAPI, resource, activeCategoryId, activeCode]);

    // Label already-saved pinned chips: fetch the categories' tags they came from.
    React.useEffect(() => {
        const unknown = chips.filter((c) => c.tag_id != null && !tagsById[c.tag_id]);
        if (unknown.length === 0 || categories.length === 0) return;
        Promise.all(categories.map((c) => dataAPI(GET_LIST, resource, {
            urlSuffix: 'tag_options/', pagination: { page: 1, perPage: 100 },
            filter: { category_id: c.id },
        }).then((res) => res.data || []).catch(() => [])))
            .then((lists) => setTagsById((prev) => lists.flat().reduce(
                (acc, t) => ({ ...acc, [t.id]: t }), { ...prev },
            )));
        // categories/chips are the only inputs; tagsById is intentionally not a dep
        // (it is what this effect writes).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataAPI, categories, chips.length]);

    const set = (patch) => onChange({ ...EMPTY_TAG_CONDITION, ...value, ...patch });

    const resetBuilder = () => {
        setCategory(null); setCategoryCode(''); setTag(null); setTagName('');
    };

    const pendingChip = isTenantScope
        ? (tag ? { tag_id: tag.id } : null)
        : (activeCode && tagName.trim()
            ? { category_code: activeCode, tag_name: tagName.trim() }
            : null);

    const addChip = () => {
        if (!pendingChip) return;
        const key = chipKey(pendingChip);
        if (chips.some((c) => chipKey(c) === key)) { resetBuilder(); return; }
        set({ chips: [...chips, pendingChip] });
        resetBuilder();
    };

    const removeChip = (key) => set({ chips: chips.filter((c) => chipKey(c) !== key) });

    const sourceOptions = (registry && registry.tag_sources) || [];
    const sourceNames = sourceOptions.reduce((acc, s) => ({ ...acc, [s.codename]: s.name }), {});

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
                {caption}
            </Typography>

            {/* committed chips + the AND/OR toggle */}
            <Box sx={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5,
                minHeight: 40, px: 1, py: 0.5, borderRadius: 1, border: 1,
                borderColor: error ? 'error.main' : 'divider',
            }}>
                {chips.length === 0 && (
                    <Typography variant="body2" color="text.secondary">No tags yet</Typography>
                )}
                {chips.map((chip) => {
                    const key = chipKey(chip);
                    const pinned = chip.tag_id != null;
                    return (
                        <Tooltip key={key} title={pinned
                            ? 'Pinned to this exact tag'
                            : (allowSpecChips
                                ? 'Matches by category code + tag name (may not exist yet)'
                                : 'Matched by category code + tag name, set in the Ops Console')}>
                            <Chip size="small" variant="outlined" label={chipLabel(chip, tagsById)}
                                onDelete={() => removeChip(key)}
                                sx={pinned ? { fontWeight: 500 } : { borderStyle: 'dashed' }} />
                        </Tooltip>
                    );
                })}
                <Box sx={{ ml: 'auto' }}>
                    <Tooltip title="Require ALL tags to match (AND), or ANY of them (OR)">
                        <ToggleButtonGroup size="small" exclusive value={matchMode}
                            disabled={chips.length < 2}
                            onChange={(e, v) => { if (v) set({ match_mode: v }); }}
                            sx={{ '& .MuiToggleButton-root': { py: 0, px: 0.75, fontSize: 11, border: 0 } }}>
                            <ToggleButton value="and">AND</ToggleButton>
                            <ToggleButton value="or">OR</ToggleButton>
                        </ToggleButtonGroup>
                    </Tooltip>
                </Box>
            </Box>

            {/* builder row */}
            <Stack direction="row" spacing={1} alignItems="flex-start">
                {allowSpecChips && (
                    <TextField select size="small" label="Tag of" value={scope} sx={{ minWidth: 150 }}
                        onChange={(e) => { setScope(e.target.value); resetBuilder(); }}>
                        <MenuItem value={SCOPE_TENANT}>This operator&apos;s tags</MenuItem>
                        <MenuItem value={SCOPE_ANY}>By name (category + tag)</MenuItem>
                    </TextField>
                )}

                {isTenantScope ? (
                    <Autocomplete size="small" sx={{ flex: 1 }} options={categories} value={category}
                        getOptionLabel={(c) => c.name || c.code}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        onChange={(_, v) => { setCategory(v); setTag(null); }}
                        renderInput={(params) => <TextField {...params} label="Category" />} />
                ) : (
                    <Autocomplete size="small" freeSolo sx={{ flex: 1 }}
                        options={categories.map((c) => c.code)} value={categoryCode}
                        onInputChange={(_, v) => { setCategoryCode(v || ''); setTagName(''); }}
                        renderInput={(params) => <TextField {...params} label="Category code"
                            helperText="e.g. reseller" />} />
                )}

                {isTenantScope ? (
                    <Autocomplete size="small" sx={{ flex: 1 }} options={tagOptions} value={tag}
                        disabled={!category}
                        getOptionLabel={(t) => t.name}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        onChange={(_, v) => setTag(v)}
                        renderInput={(params) => <TextField {...params} label="Tag" />} />
                ) : (
                    <Autocomplete size="small" freeSolo sx={{ flex: 1 }}
                        options={tagOptions.map((t) => t.name)} value={tagName}
                        onInputChange={(_, v) => setTagName(v || '')}
                        renderInput={(params) => <TextField {...params} label="Tag name"
                            helperText="Matched case-insensitively" />} />
                )}

                <Chip label="Add" color="primary" variant={pendingChip ? 'filled' : 'outlined'}
                    onClick={addChip} disabled={!pendingChip} sx={{ mt: 0.5 }} />
            </Stack>

            {/* sources */}
            {!hideSources && (
                <TextField select required size="small" label="Tag sources"
                    SelectProps={{
                        multiple: true,
                        renderValue: (sel) => sel.map((c) => sourceNames[c] || c).join(', '),
                    }}
                    value={sources}
                    onChange={(e) => set({ sources: e.target.value })}
                    error={sources.length === 0}
                    helperText={sources.length === 0
                        ? 'Pick at least one source.'
                        : 'The pool is the union of these — a wider selection blocks more.'}>
                    {sourceOptions.map((s) => (
                        <MenuItem key={s.codename} value={s.codename} dense>
                            <Checkbox size="small" checked={sources.includes(s.codename)} sx={{ py: 0, mr: 1 }} />
                            <ListItemText primary={s.name} secondary={s.codename} />
                        </MenuItem>
                    ))}
                </TextField>
            )}

            {!allowSpecChips && chips.some((c) => c.tag_id == null) && (
                <Alert severity="info" sx={{ py: 0 }}>
                    The dashed chips match by category code + tag name and were set in the Ops
                    Console. They are kept as they are — remove one if it should no longer apply,
                    but new tags you add here are always this operator&apos;s own tags.
                </Alert>
            )}
            {allowSpecChips && chips.some((c) => c.tag_id == null) && (
                <Alert severity="info" sx={{ py: 0 }}>
                    Dashed chips match by category code + tag name, so they also cover tags
                    created later.
                </Alert>
            )}
        </Box>
    );
};

export default TagConditionField;
